import React from "react";
import { fmtMins } from "../../../components/classic/micro";
import { segmentProse } from "../../../components/drilldown/WorkLog";
import { inks, paper } from "../viz";
import { ToolIcon, brandColorFor } from "../../../components/classic/panels";
import { toolGlyphIdFor } from "../../../components/classic/toolIcons";

/** Rule 2: a tool gets its icon; an unknown label never gets initials. */
const knownTool = (t, name) => Boolean(name && (toolGlyphIdFor(name) || brandColorFor(name, t)));

/**
 * TimelineKit — the ONE timeline grammar for every drill-down list.
 * Approved via DRILLDOWN_TIMELINE_MOCK.html (repo root, kept as reference).
 *
 * The day is a timeline, so every list of timed work draws as one: a time
 * gutter on the left, a project/person-inked node on a vertical rail, and
 * the entry itself as a card whose written analysis renders as BEATS (the
 * segmentProse split — same words, never a paragraph), with untracked gaps
 * shown as dashed rows and multi-day windows grouped under day dividers.
 *
 * Honesty rules carried over from the mock:
 *   - nothing invented: rows without real times get no gutter and no gaps;
 *   - unclear stretches keep their amber "couldn't be named" treatment;
 *   - gaps are "Nm with no tracked activity", never named (no "lunch?").
 *
 * Consumers: person work log, tool What-was-done / Written stretches,
 * project sitting lists, OrgToday sittings. Row builders for the two data
 * shapes (t0/t1 "HH:MM" stretches, startMs/endMs sittings) live here so
 * every surface maps the same way.
 */

/* ── time helpers ────────────────────────────────────────────────────────── */

/** "09:40" / "14:02:06" / "2:02 pm" → minutes since local midnight, else null. */
export function parseHM(s) {
  const m = /^\s*(\d{1,2}):(\d{2})(?::\d{2})?\s*(am|pm)?/i.exec(String(s || ""));
  if (!m) return null;
  let h = Number(m[1]);
  const mm = Number(m[2]);
  const ap = (m[3] || "").toLowerCase();
  if (ap === "pm" && h < 12) h += 12;
  if (ap === "am" && h === 12) h = 0;
  if (h > 23 || mm > 59) return null;
  return h * 60 + mm;
}

/** Minutes-since-midnight → "9:12 AM" (the graphical pages' clock style). */
export function fmt12(min) {
  if (!Number.isFinite(min)) return "";
  const h = Math.floor(min / 60) % 24;
  const mm = String(Math.round(min % 60)).padStart(2, "0");
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${mm} ${h < 12 ? "AM" : "PM"}`;
}

/** "2026-08-22" → "Fri, Aug 22" (day divider label). */
function prettyDayLabel(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
  if (!m) return String(iso || "");
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

const GAP_MIN = 15; // smaller holes are breathing, not absence

/* ── beats — the paragraph, split; words unchanged ───────────────────────── */

export function Beats({ t, text, beats, ink = "blue", max = 3 }) {
  const k = inks(t);
  const rows = React.useMemo(() => {
    if (beats && beats.length) return beats.map((b) => [{ kind: "text", value: String(b) }]);
    return segmentProse(text);
  }, [beats, text]);
  if (!rows.length) return null;

  const chip = (v, key) => (
    <code key={key} title={v} style={{
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
      fontSize: "0.86em", padding: "1px 6px", borderRadius: 6,
      background: paper(t).well, color: t.c.text,
      boxShadow: `inset 0 0 0 1px ${t.c.border}`,
      wordBreak: "break-all", lineHeight: 1.45,
    }}>{v}</code>
  );

  return (
    <ul style={{ margin: "10px 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 7 }}>
      {rows.slice(0, max).map((beat, bi) => (
        <li key={bi} style={{ display: "grid", gridTemplateColumns: "14px 1fr", gap: 8, alignItems: "start" }}>
          <i aria-hidden="true" style={{
            width: 5, height: 5, borderRadius: 99, marginTop: 8, justifySelf: "center",
            background: k[ink] || k.blue,
          }} />
          <span style={{ fontSize: 13, lineHeight: 1.6, color: t.c.textSecondary, maxWidth: "74ch" }}>
            {beat.map((tok, i) => (tok.kind === "artefact"
              ? chip(tok.value, `${bi}-${i}`)
              : <React.Fragment key={`${bi}-${i}`}>{tok.value}</React.Fragment>))}
          </span>
        </li>
      ))}
      {rows.length > max ? (
        <li style={{ fontSize: 11.5, color: t.c.textTertiary, paddingLeft: 22 }}>
          +{rows.length - max} more in the full write-up
        </li>
      ) : null}
    </ul>
  );
}

/* ── the micro app-mix bar inside a card ─────────────────────────────────── */

const MIX_CYCLE = ["blue", "violet", "gold", "pink"];

export function MixBar({ t, parts }) {
  const k = inks(t);
  const list = (parts || []).filter((p) => p && Number(p.pct) > 0).slice(0, 4);
  if (!list.length) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 11 }}>
      <div style={{ flex: 1, display: "flex", height: 6, borderRadius: 99, overflow: "hidden", background: paper(t).well }}>
        {list.map((p, i) => (
          <i key={i} style={{ display: "block", height: "100%", width: `${p.pct}%`, background: k[p.ink || MIX_CYCLE[i % MIX_CYCLE.length]] }} />
        ))}
      </div>
      <span style={{ fontSize: 10.5, color: t.c.textTertiary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "46%" }}>
        {list.map((p) => `${p.label} ${p.pct}%`).join(" · ")}
      </span>
    </div>
  );
}

/* ── pills ───────────────────────────────────────────────────────────────── */

function Pill({ t, tone = "muted", children }) {
  const k = inks(t);
  const map = {
    dur: { bg: t.c.primarySoft, fg: k.blue, w: 650 },
    project: { bg: t.c.aiSoft || `${k.violet}1c`, fg: k.violet, w: 650 },
    warn: { bg: (t.status && t.status.warningSoft) || `${k.gold}1c`, fg: (t.status && t.status.warning) || k.gold, w: 650 },
    muted: { bg: `${k.teal}1c`, fg: k.teal, w: 650 },
    ok: { bg: `${(t.status && t.status.positive) || k.teal}1c`, fg: (t.status && t.status.positive) || k.teal, w: 650 },
  };
  const s = map[tone] || map.muted;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", height: 21, padding: "0 9px",
      borderRadius: 99, fontSize: 11, fontWeight: s.w, whiteSpace: "nowrap",
      background: s.bg, color: s.fg, fontVariantNumeric: "tabular-nums",
      maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis",
    }}>{children}</span>
  );
}

/** Intensity → node/dot colour. An unclear stretch is never green. */
function intensityColor(t, row) {
  const k = inks(t);
  if (row.unclear) return (t.status && t.status.warning) || k.gold;
  if (row.intensity === "high") return (t.status && t.status.positive) || k.blue;
  if (row.intensity === "low") return k.teal;
  return k.blue;
}

/* ── row builders — the two data shapes, mapped ONE way ──────────────────── */

/**
 * Person/tool stretches (worklog / tool-detail shape: t0/t1 "HH:MM",
 * dateLocal, title, summary, project, durationLabel, intensity, unclear,
 * activities[], userName). `inkFor(projectName)` keeps the ink identical to
 * the ZoomInstrument segments; `idFor(st, i)` keeps hotIds shared with it.
 */
export function railFromStretches(stretches, { inkFor, idFor } = {}) {
  return (stretches || []).map((st, i) => {
    const s = parseHM(st.t0);
    const e = parseHM(st.t1);
    const acts = (st.activities || []).filter((a) => a && Number(a.weight) > 0);
    const totalW = acts.reduce((a, x) => a + Number(x.weight), 0);
    return {
      key: st.id || `st-${i}`,
      id: idFor ? idFor(st, i) : (st.id || `st-${i}`),
      index: i,
      raw: st,
      day: st.dateLocal || st.day || null,
      startMin: s,
      endMin: e != null && s != null && e > s ? e : null,
      t0Label: s != null ? fmt12(s) : (st.t0 || null),
      t1Label: e != null ? fmt12(e) : (st.t1 || null),
      ink: inkFor ? inkFor(st.project) : "blue",
      title: st.title || null,
      unclear: Boolean(st.unclear) || !st.title,
      intensity: st.intensity,
      project: st.project || null,
      who: st.userName ? String(st.userName).split(" ")[0] : null,
      app: st.app || (acts[0] && acts[0].label) || null,
      durationLabel: st.durationLabel || (Number(st.minutes) > 0 ? fmtMins(st.minutes) : null),
      summary: st.summary || null,
      mix: totalW > 0 ? acts.slice(0, 3).map((a, j) => ({
        label: a.label, pct: Math.round((Number(a.weight) / totalW) * 100), ink: MIX_CYCLE[j % MIX_CYCLE.length],
      })) : null,
    };
  });
}

/**
 * Sittings (project/org-today shape: startMs/endMs, name, what/title,
 * minutes/timeLabel, analysed, day). `inkMap` + `idFor` keep ink and hotId
 * identical to the instrument's segments on that page.
 */
export function railFromSittings(sittings, { inkMap, idFor } = {}) {
  return (sittings || []).map((st, i) => {
    const a = Number(st.startMs);
    const b = Number(st.endMs);
    const has = Number.isFinite(a) && Number.isFinite(b) && b > a;
    const d = has ? new Date(a) : null;
    const startMin = has ? d.getHours() * 60 + d.getMinutes() : null;
    let endMin = has ? Math.min(1440, startMin + Math.max(1, Math.round((b - a) / 60000))) : null;
    const id = idFor ? idFor(st, i) : `${st.userId}.${st.startMs}`;
    return {
      key: st.key || `${st.userId}-${st.startMs || st.day || i}`,
      id,
      index: i,
      raw: st,
      day: st.day || (d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : null),
      startMin,
      endMin,
      t0Label: startMin != null ? fmt12(startMin) : (st.day ? String(st.day).slice(5) : null),
      t1Label: endMin != null ? fmt12(endMin) : null,
      ink: (inkMap && inkMap[id]) || "blue",
      title: st.what || st.title || "A sitting",
      sub: st.title && st.what && st.title !== st.what ? st.title : null,
      unclear: st.analysed === false,
      unclearLabel: st.analysed === false ? "measured only" : null,
      who: st.name ? String(st.name).split(" ")[0] : null,
      app: st.app || null,
      tag: null,
      durationLabel: st.timeLabel || (Number(st.minutes) > 0 ? fmtMins(st.minutes) : null),
      summary: null,
    };
  });
}

/* ── the rail itself ─────────────────────────────────────────────────────── */

const GUTTER = 76;
const NODE = 26;

/**
 * `gaps` — dashed "no tracked activity" rows between entries. Only honest on a
 * SINGLE-SUBJECT list (one person's day): on a multi-person rail a hole
 * between two rows says nothing about the org, so those callers pass false.
 */
export function TimeRail({ t, rows, onOpen, hotId, onHot, maxBeats = 3, gaps = true }) {
  const k = inks(t);
  const ppr = paper(t);
  const list = rows || [];
  if (!list.length) return null;

  const days = [...new Set(list.map((r) => r.day).filter(Boolean))];
  const multiDay = days.length > 1;

  const out = [];
  let prev = null;
  for (const row of list) {
    if (multiDay && row.day && (!prev || prev.day !== row.day)) {
      out.push({ kind: "day", key: `day-${row.day}`, label: prettyDayLabel(row.day) });
    } else if (gaps && prev && !multiDay
      && Number.isFinite(prev.endMin) && Number.isFinite(row.startMin)
      && row.startMin - prev.endMin >= GAP_MIN) {
      out.push({
        kind: "gap",
        key: `gap-${prev.key}-${row.key}`,
        minutes: row.startMin - prev.endMin,
        span: `${fmt12(prev.endMin)} – ${fmt12(row.startMin)}`,
      });
    }
    out.push({ kind: "row", key: row.key, row });
    prev = row;
  }

  const grid = { display: "grid", gridTemplateColumns: `${GUTTER}px ${NODE}px 1fr`, gap: 0 };

  return (
    <div className="tgw-race" style={{ position: "relative", padding: "6px 0 2px" }}>
      {/* the rail — fades in and out so it never implies time beyond the data */}
      <i aria-hidden="true" style={{
        position: "absolute", left: GUTTER + NODE / 2 - 1, top: 14, bottom: 10, width: 2,
        background: `linear-gradient(180deg, transparent, ${t.c.border} 24px, ${t.c.border} calc(100% - 24px), transparent)`,
      }} />

      {out.map((item) => {
        if (item.kind === "day") {
          return (
            <div key={item.key} style={{ ...grid, margin: "8px 0 2px" }}>
              <span /><span />
              <div style={{
                display: "flex", alignItems: "center", gap: 10, fontSize: 11, fontWeight: 800,
                letterSpacing: "0.08em", textTransform: "uppercase", color: t.c.textTertiary,
              }}>
                {item.label}
                <i style={{ flex: 1, height: 1, background: t.c.border }} />
              </div>
            </div>
          );
        }
        if (item.kind === "gap") {
          return (
            <div key={item.key} style={grid}>
              <span /><span />
              <div style={{
                margin: "2px 0", padding: "7px 15px", fontSize: 11.5, color: t.c.textTertiary,
                borderRadius: 10, border: `1px dashed ${t.c.border}`,
                display: "flex", gap: 8, alignItems: "center",
              }}>
                ⋯ <b style={{ color: t.c.textSecondary, fontWeight: 650 }}>{fmtMins(item.minutes)}</b>
                &nbsp;with no tracked activity · {item.span}
              </div>
            </div>
          );
        }

        const row = item.row;
        const hot = hotId != null && row.id === hotId;
        const nodeInk = row.unclear ? ((t.status && t.status.warning) || k.gold) : (k[row.ink] || k.blue);
        return (
          <div key={item.key} style={grid}>
            <div style={{
              textAlign: "right", paddingTop: 17, paddingRight: 2, fontSize: 12, fontWeight: 700,
              color: t.c.textSecondary, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
            }}>
              {row.t0Label || "—"}
              {row.t1Label ? (
                <span style={{ display: "block", fontSize: 10.5, fontWeight: 600, color: t.c.textTertiary, marginTop: 2 }}>
                  – {row.t1Label}
                </span>
              ) : null}
            </div>
            <div style={{ position: "relative" }}>
              <i aria-hidden="true" style={{
                position: "absolute", left: "50%", top: 21, transform: "translateX(-50%)",
                width: 11, height: 11, borderRadius: 99, background: nodeInk,
                boxShadow: `0 0 0 3px ${ppr.card}, 0 0 0 4px ${t.c.border}`,
              }} />
            </div>
            <button
              type="button"
              className="tgw-row"
              onClick={onOpen ? () => onOpen(row, row.index) : undefined}
              disabled={!onOpen}
              onMouseEnter={onHot ? () => onHot(row.id) : undefined}
              onMouseLeave={onHot ? () => onHot(null) : undefined}
              onFocus={onHot ? () => onHot(row.id) : undefined}
              onBlur={onHot ? () => onHot(null) : undefined}
              style={{
                display: "block", width: "100%", textAlign: "left", margin: "5px 0",
                padding: "12px 14px 11px", borderRadius: t.r.well, fontFamily: "inherit", color: "inherit",
                background: hot ? ppr.well : ppr.card, border: `1px solid ${hot ? nodeInk : t.c.border}`,
                cursor: onOpen ? "pointer" : "default",
                transform: hot ? "translateX(3px)" : "none",
                transition: "transform .14s ease, border-color .14s ease, background .14s ease",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
                {row.app && knownTool(t, row.app) ? (
                  <span aria-hidden="true" title={row.app} style={{ display: "inline-flex", borderRadius: 9, flexShrink: 0, boxShadow: `0 0 0 2px ${intensityColor(t, row)}55` }}>
                    <ToolIcon name={row.app} size={26} />
                  </span>
                ) : (
                  <i aria-hidden="true" style={{ width: 9, height: 9, borderRadius: 99, background: intensityColor(t, row), flexShrink: 0 }} />
                )}
                <span style={{
                  fontSize: 14, fontWeight: 700, letterSpacing: "-0.2px", marginRight: "auto",
                  minWidth: 0, color: row.unclear && !row.unclearLabel ? t.c.textSecondary : t.c.text,
                }}>
                  {row.title || "Couldn’t be named"}
                </span>
                {row.who ? <Pill t={t} tone="muted">{row.who}</Pill> : null}
                {row.project ? <Pill t={t} tone="project">{row.project}</Pill> : null}
                {row.tag ? <Pill t={t} tone="project">{row.tag}</Pill> : null}
                {row.unclear ? <Pill t={t} tone="warn">{row.unclearLabel || "not yet analysed"}</Pill> : <Pill t={t} tone="ok">analysed</Pill>}
                {row.durationLabel ? <Pill t={t} tone="dur">{row.durationLabel}</Pill> : null}
                {onOpen ? <span style={{ fontSize: 12, fontWeight: 800, color: k.blue }}>›</span> : null}
              </span>
              {row.sub ? (
                <span style={{ display: "block", fontSize: 12, color: t.c.textTertiary, marginTop: 3 }}>{row.sub}</span>
              ) : null}
              {row.unclear && !row.unclearLabel ? (
                <span style={{ display: "block", fontSize: 12.5, lineHeight: 1.55, color: t.c.textSecondary, marginTop: 6 }}>
                  The time is real and counted; nothing in it was distinctive enough to name the work. No title was invented.
                </span>
              ) : row.summary ? (
                <Beats t={t} text={row.summary} ink={row.ink} max={maxBeats} />
              ) : null}
              {row.mix ? <MixBar t={t} parts={row.mix} /> : null}
            </button>
          </div>
        );
      })}
    </div>
  );
}

export default TimeRail;
